// Sprint md-doc-viewer (D-012) — URL-driven state for the doc viewer sheet.
//
// State channel: `?doc=<relative-path>&agent=<agent-id>` (D-006). Reload
// preserves the open doc per cursor-ux-mirror §2.2.
//
// We read/write window.location directly (via useSyncExternalStore +
// pushState) instead of react-router-dom's useSearchParams, so the hook is
// callable anywhere in the tree — including unit-test environments that
// render organisms without a Router wrapper. React Router shares the same
// underlying history object, so navigation stays consistent.

import { useCallback, useSyncExternalStore } from 'react';
import { useAppStore } from '../stores/app.store';

export interface DocViewerState {
  /** The doc currently being shown, or null when closed. */
  current: { path: string; agentId: string } | null;
  /** Open `path` for the currently-selected agent. No-op if no agent. */
  openDoc: (path: string) => void;
  /** Replace the open path (used by internal markdown link navigation). */
  navigateTo: (path: string) => void;
  /** Close the viewer. Clears both query params. */
  close: () => void;
}

const URL_EVENT = 'as-oasis:url-search-changed';

// Stable snapshot cache for useSyncExternalStore. The hook compares
// snapshots with Object.is between renders; returning a fresh `{ path,
// agentId }` every call drives an infinite re-render loop in any
// subscriber whose tree re-renders while `?doc=` is set. Cache the last
// non-null snapshot so identity is preserved when content is unchanged.
// Discovered while wiring ToolCallRow (sprint md-viewer-coverage-gaps).
let cachedDocSnapshot: { path: string; agentId: string } | null = null;

function readDocFromUrl(): { path: string; agentId: string } | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const path = params.get('doc');
  const agentId = params.get('agent');
  if (!path || !agentId) {
    cachedDocSnapshot = null;
    return null;
  }
  if (
    cachedDocSnapshot !== null &&
    cachedDocSnapshot.path === path &&
    cachedDocSnapshot.agentId === agentId
  ) {
    return cachedDocSnapshot;
  }
  cachedDocSnapshot = { path, agentId };
  return cachedDocSnapshot;
}

function subscribeToUrl(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('popstate', cb);
  window.addEventListener(URL_EVENT, cb);
  return () => {
    window.removeEventListener('popstate', cb);
    window.removeEventListener(URL_EVENT, cb);
  };
}

function updateUrlSearch(mutate: (params: URLSearchParams) => void): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  mutate(url.searchParams);
  window.history.pushState({}, '', url.toString());
  window.dispatchEvent(new Event(URL_EVENT));
}

export function useDocViewer(): DocViewerState {
  const current = useSyncExternalStore(
    subscribeToUrl,
    readDocFromUrl,
    () => null,
  );
  const selectedAgentId = useAppStore((s) => s.selectedAgentId);

  const openDoc = useCallback(
    (path: string) => {
      if (!selectedAgentId) return;
      updateUrlSearch((params) => {
        params.set('doc', path);
        params.set('agent', selectedAgentId);
      });
    },
    [selectedAgentId],
  );

  const navigateTo = useCallback(
    (path: string) => {
      const agent = current?.agentId ?? selectedAgentId;
      if (!agent) return;
      updateUrlSearch((params) => {
        params.set('doc', path);
        params.set('agent', agent);
      });
    },
    [current?.agentId, selectedAgentId],
  );

  const close = useCallback(() => {
    updateUrlSearch((params) => {
      params.delete('doc');
      params.delete('agent');
    });
  }, []);

  return { current, openDoc, navigateTo, close };
}
